import { exists } from "../utils/Safe";
import { ChannelModel, TrackModel, getAudioTrackModel } from "./AudioModels";

/**
 * A group of audio.
 */
export default class AudioChannel
{
    /**
     * Returns whether or not the channel has track data.
     * @type {boolean}
     * @readonly
     */
    get hasTracks() { return Object.keys(this.data.trackMap).length > 0; }

    /**
     * @param {ChannelModel} data
     */
    constructor(data)
    {
        /**
         * Contains all information of a channel from the config.
         * @type {ChannelModel}
         */
        this.data = data;
        /**
         * A map of inactive Phaser.Sound objects.
         * @type {Object.<string, Phaser.Sound[]>}
         */
        this.trackPool = { };
        /**
         * A map of playing Phaser.Sound objects.
         * @type {Object.<string, Phaser.Sound[]>}
         */
        this.playingTracks = { };
        /**
         * The channel's volume before muting.
         * @type {number}
         */

        this.unmutedVolume = this.data.volume;
        this.onSoundPlayed = new Phaser.Signal();
    }

    /**
     * Cleans up the AudioChannel.
     */
    destroy()
    {
        // Remove all tracks from this channel. This will also clean up playing and pooled tracks.
        this.removeTracks(this.data.trackMap);
        this.onSoundPlayed.dispose();
        this.onSoundPlayed = null;

        // Null the model reference
        this.data = null;
    }

    /**
     * Appends TrackModel objects to the channel's track map. The provided
     * track map can be a dynamic object, so this function will make sure
     * it exists before attempting to append tracks.
     * @param {Object.<string, *>} trackMap
     */
    appendTracks(trackMap)
    {
        if (exists(trackMap))
        {
            for (let key in trackMap)
            {
                if (exists(this.data.trackMap[key]))
                {
                    console.warn(`[AudioChannel] Failed to append. Duplicate track key ${key} found.`);
                }
                else
                {
                    this.data.trackMap[key] = getAudioTrackModel(trackMap[key]);
                }
            }
        }
    }

    /**
     * Removes TrackModel objects from the channel's track map and destroys and associated Phaser.Sound objects.
     * @param {*} trackMap
     */
    removeTracks(trackMap)
    {
        if (exists(trackMap))
        {
            let keys = Object.keys(trackMap);
            while (keys.length > 0)
            {
                let key = keys.pop();

                // Clean up playing tracks.
                if (exists(this.playingTracks[key]))
                {
                    // Empty the lists and destroy all Phaser.Sound objects.
                    while (this.playingTracks[key].length > 0)
                    {
                        this.playingTracks[key][0].onStop.removeAll();
                        this.playingTracks[key][0].destroy();
                        this.playingTracks[key].shift();
                    }
                    delete this.playingTracks[key];
                }
                // Clean up pooled tracks.
                if (exists(this.trackPool[key]))
                {
                    // Empty the pools and destroy all Phaser.Sound objects.
                    while (this.trackPool[key].length > 0)
                    {
                        if(this.trackPool[key][0])
                        {
                            this.trackPool[key][0].destroy();
                        }
                        this.trackPool[key].shift();
                    }
                    delete this.trackPool[key];
                }
                // Clean up the data object.
                if (exists(this.data.trackMap[key]))
                {
                    // TODO - Clean up pool and playing track arrays.
                    delete this.data.trackMap[key];
                }
            }
        }
    }

    /**
     * Returns whether or not this channel has the track specified.
     * @param {string} trackKey
     */
    hasTrack(trackKey)
    {
        return exists(this.data.trackMap[trackKey]);
    }

    /**
     *
     * @param {Phaser.Game} game
     * @param {*} trackKey
     * @param {function} onComplete
     * @param {*} onCompleteCtx
     */
    playTrack(game, trackKey, onComplete = null, onCompleteCtx = null)
    {
        // Check if the track exists in the channel.
        if (exists(this.data.trackMap[trackKey]))
        {
            // Checking if a single track channel is already playing a sound
            if(this.data.singleTrack)
            {
                let sound = null;
                for(var key in this.playingTracks)
                {
                    var array = this.playingTracks[key];
                    if(this.playingTracks[key].length > 0)
                    {
                        if(this.data.trackMap[trackKey].priority >= this.data.trackMap[array[0].name].priority)
                        {
                            this.stopTrack(key);
                            break;
                        }
                        else
                        {
                            return false;
                        }
                    }
                }
            }
            // Get the next free Phaser.Sound object. If none are free, then a new one is created.
            let sound = this.getNextTrackFromPool(game, trackKey);

            // Add the internal onStop callback.
            sound.onStop.addOnce(
                function()
                {
                    // Return the complete Phaser.Sound object to its pool.
                    this.returnTrackToPool(trackKey, sound);

                    // Call the playback's complete event.
                    if (onComplete !== null)
                    {
                        onComplete.call(onCompleteCtx);
                    }
                },
            this);

            // Check if the a list of playing tracks for this sound exists, create one it is doesn't.
            if (!exists(this.playingTracks[trackKey]))
            {
                this.playingTracks[trackKey] = [];
            }

            // Add the sound to the playing track list and play the sound.
            this.playingTracks[trackKey].push(sound.play());
            this.onSoundPlayed.dispatch(sound.key);

            // Success!
            return true;
        }
        return false;
    }

    /**
     * Stops all playing Phaser.Sound objects associated with the provided track key.
     * @param {string} trackKey
     * @param {boolean} [allowCallbacks=false]
     */
    stopTrack(trackKey, allowCallbacks = false)
    {
        // Check if the track exists in the channel.
        if (exists(this.data.trackMap[trackKey]))
        {
            // Check if there are playing tracks.
            if (exists(this.playingTracks[trackKey]) && this.playingTracks[trackKey].length > 0)
            {
                // Stop all Phaser.Sound object associated with the track key.
                while (this.playingTracks[trackKey].length > 0)
                {
                    // Check if stopping a track is allowed to call its callback.
                    if (!allowCallbacks)
                    {
                        // Remove the callbacks before stopping the sound.
                        this.playingTracks[trackKey][0].onStop.removeAll();
                        this.playingTracks[trackKey][0].stop();

                        // This happens in the onStop callback which we just removed, so manually return it.
                        this.returnTrackToPool(trackKey, this.playingTracks[trackKey][0]);
                    }
                    else
                    {
                        // Stop the sound.
                        this.playingTracks[trackKey][0].stop();
                    }
                }
            }
        }
    }

    /**
     * Stops all playing Phaser.Sound objects within this channel.
     * @param {boolean} [allowCallbacks=false]
     */
    stopAll(allowCallbacks = false)
    {
        for (let key in this.data.trackMap)
        {
            this.stopTrack(key, allowCallbacks);
        }
    }

    /**
     * Sets the channel volume to the provided value.
     * @param {number} volume
     */
    setVolume(volume)
    {
        this.data.volume = volume;
        this.unmutedVolume = volume;

        // Update the volume of any playing tracks.
        for (let key in this.playingTracks)
        {
            for (let i = 0; i < this.playingTracks[key].length; i++)
            {
                this.playingTracks[key][i].volume = volume;
            }
        }
    }

    mute()
    {
        this.data.volume = 0;
        for (let key in this.playingTracks)
        {
            for (let i = 0; i < this.playingTracks[key].length; i++)
            {
                this.playingTracks[key][i].volume = 0;
            }
        }
    }

    unmute()
    {
        this.data.volume = this.unmutedVolume;
        for (let key in this.playingTracks)
        {
            for (let i = 0; i < this.playingTracks[key].length; i++)
            {
                this.playingTracks[key][i].volume = this.unmutedVolume;
            }
        }
    }

    /**
     * Used internally to get a track from a pool.
     * @param {Phaser.Game} game
     * @param {string} trackKey
     */
    getNextTrackFromPool(game, trackKey)
    {
        let volume = this.data.trackMap[trackKey].volume * this.data.volume;
        let looping = this.data.trackMap[trackKey].looping;

        // Check if a pool exists for this track and if there are tracks in it.
        if (exists(this.trackPool[trackKey]) && this.trackPool[trackKey].length > 0)
        {
            let sound = this.trackPool[trackKey].shift();
            // Keep the Phaser.Sound object's volume up to date.
            sound.volume = volume;
            // Return the next track of the pool.
            return sound;
        }

        // Create a new track.
        return game.add.audio(trackKey, volume, looping);
    }

    /**
     * Used internally to return a Phaser.Sound object back to its pool
     * after it is finished playing.
     * @param {string} trackKey
     * @param {Phaser.Sound} track
     */
    returnTrackToPool(trackKey, track)
    {
        // Remove the Phaser.Sound object from the playing track list.
        let index = this.playingTracks[trackKey].indexOf(track);
        this.playingTracks[trackKey].splice(index, 1);

        // Check if a pool exists for this track. Create one if it doesn't.
        if (!exists(this.trackPool[trackKey]))
        {
            this.trackPool[trackKey] = [];
        }

        // Add the Phaser.Sound object to the pool.
        this.trackPool[trackKey].push(track);
    }
}
